/**
 * API hooks for venture file operations
 * Provides file upload, download, and management for ventures
 */
import { createHeaders, useServerState } from "@project-mgmt/api-client";
/**
 * Hook to get files for a venture
 * Requirement 5.4: File listing
 */
export function useVentureFiles(ventureId, workspaceId) {
    return useServerState({
        key: ["ventures", ventureId, "files"],
        fetcher: async (_, signal) => {
            if (!ventureId || !workspaceId)
                throw new Error("Venture ID and Workspace ID are required");
            const response = await fetch(`/api/ventures/${ventureId}/files`, {
                signal,
                headers: createHeaders(workspaceId),
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch venture files: ${response.statusText}`);
            }
            return response.json();
        },
        enabled: !!ventureId && !!workspaceId,
    });
}
/**
 * Hook to prepare file upload for a venture
 * Requirement 5.1, 5.2: File upload preparation
 */
export function useVentureFileUpload(ventureId, workspaceId) {
    return useServerState({
        key: ["ventures", ventureId, "files", "upload"],
        fetcher: async (uploadRequest, signal) => {
            // Include ventureId in the request body for backend validation
            const requestBody = {
                ...uploadRequest,
                ventureId,
            };
            const response = await fetch(`/api/ventures/${ventureId}/files`, {
                method: "POST",
                signal,
                headers: createHeaders(workspaceId),
                body: JSON.stringify(requestBody),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData?.error || "Failed to prepare upload");
            }
            return response.json();
        },
        manual: true,
    });
}
/**
 * Direct upload function for venture files
 * Requirement 5.1, 5.2, 5.3: Direct file upload with progress
 */
export async function uploadVentureFileDirect(ventureId, workspaceId, file, fileId, onProgress) {
    const uploadUrl = `/api/ventures/${ventureId}/files/direct/${fileId}`;
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable && onProgress) {
                const progress = (event.loaded / event.total) * 100;
                onProgress(progress);
            }
        });
        xhr.addEventListener("load", () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response);
                }
                catch (error) {
                    reject(new Error("Invalid response format"));
                }
            }
            else {
                reject(new Error(`Upload failed with status ${xhr.status}`));
            }
        });
        xhr.addEventListener("error", () => {
            reject(new Error("Upload failed due to network error"));
        });
        xhr.open("PUT", uploadUrl);
        xhr.setRequestHeader("x-filename", file.name);
        xhr.setRequestHeader("X-Workspace-ID", workspaceId);
        xhr.setRequestHeader("content-type", file.type);
        xhr.setRequestHeader("Authorization", `Bearer ${localStorage.getItem("auth_token")}`);
        xhr.send(file);
    });
}
/**
 * Hook to get file download URL
 * Requirement 5.4: File download
 */
export function useVentureFileDownload(ventureId, workspaceId) {
    return useServerState({
        key: ["ventures", ventureId, "files", "download"],
        fetcher: async (fileId, signal) => {
            const response = await fetch(`/api/ventures/${ventureId}/files/${fileId}/download`, {
                signal,
                headers: createHeaders(workspaceId),
            });
            if (!response.ok) {
                throw new Error(`Failed to get download URL: ${response.statusText}`);
            }
            return response.json();
        },
        manual: true,
    });
}
/**
 * Hook to delete a venture file
 * Requirement 5.5: File deletion
 */
export function useVentureFileDelete(ventureId, workspaceId) {
    return useServerState({
        key: ["ventures", ventureId, "files", "delete"],
        fetcher: async (fileId, signal) => {
            const response = await fetch(`/api/ventures/${ventureId}/files/${fileId}`, {
                method: "DELETE",
                signal,
                headers: createHeaders(workspaceId),
            });
            if (!response.ok) {
                throw new Error(`Failed to delete file: ${response.statusText}`);
            }
            return response.json();
        },
        manual: true,
    });
}
/**
 * Download venture file function (non-hook)
 * Requirement 5.4: File download
 */
export async function downloadVentureFile(ventureId, workspaceId, fileId) {
    try {
        const response = await fetch(`/api/ventures/${ventureId}/files/${fileId}/download`, {
            headers: createHeaders(workspaceId),
        });
        if (!response.ok) {
            throw new Error(`Failed to get download URL: ${response.statusText}`);
        }
        const { downloadUrl, filename } = await response.json();
        // Create a temporary link and trigger download
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = filename;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
    catch (error) {
        console.error("Download failed:", error);
        throw error;
    }
}
/**
 * Delete venture file function (non-hook)
 * Requirement 5.5: File deletion
 */
export async function deleteVentureFile(ventureId, workspaceId, fileId) {
    const response = await fetch(`/api/ventures/${ventureId}/files/${fileId}`, {
        method: "DELETE",
        headers: createHeaders(workspaceId),
    });
    if (!response.ok) {
        throw new Error(`Failed to delete file: ${response.statusText}`);
    }
}
